import React, { useEffect, useState, useRef, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/omega/v2`;
const USER_ID = "default";

// Auth is cookie-based; these fetch calls were missing credentials:"include"
// entirely, so no auth cookie was ever sent cross-origin (frontend and
// backend are different origins in production) — every call here 401'd.
// CSRF token: login/auth-me responses hand it back in the JSON body (since
// document.cookie can't read it cross-origin either), stashed in
// localStorage under this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

const COMFORT_LEVELS = ["LOW", "MEDIUM", "HIGH", "OMEGA_ONLY"];
const COMFORT_DESC = {
  LOW:        "Minimal real-trade exposure, strict gates",
  MEDIUM:     "Moderate real-trade access with confirmation",
  HIGH:       "Broader access, still requires manual confirm",
  OMEGA_ONLY: "Paper/ZBucks only — no real trades ever",
};

const READINESS_LABELS = {
  ZBUCKS_ONLY:           { color: "#94a3b8", label: "Learning Only" },
  PREVIEW_ONLY:          { color: "#fbbf24", label: "Preview Only" },
  MANUAL_CONFIRM_SMALL:  { color: "#60a5fa", label: "Manual Confirm" },
  ELIGIBLE_LIMITED_LIVE: { color: "#4ade80", label: "Live Eligible" },
};

// ── Swipe-to-Confirm slider ────────────────────────────────────────────────
function SwipeToConfirm({ onConfirm, disabled, color = "#4ade80", label = "SLIDE TO CONFIRM" }) {
  const trackRef = useRef(null);
  const startXRef = useRef(null);
  const [x, setX] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [dragging, setDragging] = useState(false);

  const THUMB = 64;

  const getMax = useCallback(() =>
    Math.max(1, (trackRef.current?.clientWidth || 320) - THUMB - 8), []);

  const finish = useCallback(() => {
    setConfirmed(true);
    setDragging(false);
    setTimeout(onConfirm, 380);
  }, [onConfirm]);

  const move = useCallback((clientX) => {
    if (startXRef.current === null) return;
    const max = getMax();
    const nx = Math.max(0, Math.min(max, clientX - startXRef.current));
    setX(nx);
    if (nx >= max * 0.90) finish();
  }, [finish, getMax]);

  const end = useCallback(() => {
    if (!confirmed) { setX(0); }
    setDragging(false);
    startXRef.current = null;
  }, [confirmed]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => move(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => end();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, move, end]);

  const max = getMax();
  const progress = max > 0 ? x / max : 0;

  return (
    <div
      ref={trackRef}
      style={{
        position: "relative", height: 60, borderRadius: 30,
        background: disabled
          ? "rgba(255,255,255,0.04)"
          : confirmed
          ? `${color}22`
          : `linear-gradient(90deg, ${color}14 0%, rgba(255,255,255,0.03) 100%)`,
        border: `1.5px solid ${disabled ? "rgba(255,255,255,0.1)" : confirmed ? color : color + "50"}`,
        overflow: "hidden",
        userSelect: "none",
        transition: "border-color 0.3s, box-shadow 0.3s, background 0.3s",
        boxShadow: disabled ? "none" : confirmed ? `0 0 28px ${color}50` : `0 0 14px ${color}20`,
      }}
    >
      {/* Fill */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0,
        width: THUMB + x + 4,
        background: `linear-gradient(90deg, ${color}40, ${color}10)`,
        transition: dragging ? "none" : "width 0.35s cubic-bezier(.4,0,.2,1)",
        borderRadius: 30,
        pointerEvents: "none",
      }} />

      {/* Label fades out as thumb moves right */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
        color: confirmed ? color : color + "cc",
        pointerEvents: "none",
        opacity: confirmed ? 1 : Math.max(0, 1 - progress * 2.2),
        transition: "opacity 0.15s",
      }}>
        {confirmed ? "✓ CONFIRMED — SUBMITTING" : label}
      </div>

      {/* Thumb */}
      {!confirmed && (
        <div
          onMouseDown={(e) => {
            if (disabled) return;
            e.preventDefault();
            startXRef.current = e.clientX - x;
            setDragging(true);
          }}
          onTouchStart={(e) => {
            if (disabled) return;
            startXRef.current = e.touches[0].clientX - x;
            setDragging(true);
          }}
          style={{
            position: "absolute", left: 4 + x, top: 4, bottom: 4, width: THUMB,
            borderRadius: 26,
            background: disabled
              ? "rgba(255,255,255,0.08)"
              : `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`,
            cursor: disabled ? "not-allowed" : dragging ? "grabbing" : "grab",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, color: "#050810",
            boxShadow: disabled ? "none" : `0 2px 16px ${color}55`,
            transition: dragging ? "none" : "left 0.35s cubic-bezier(.4,0,.2,1)",
            zIndex: 1,
          }}
        >
          {dragging ? "⟶" : "→"}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function RealTradePreviewModal({ trade, onClose }) {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/preview/settings/${USER_ID}`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()),
      fetch(`${API}/preview/create`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({
          user_id: USER_ID,
          symbol: trade.ticker || trade.symbol,
          side: "BUY",
          quantity: trade.kelly?.oci_contracts || trade.kelly?.contracts || 1,
          limit_price: trade.ask || trade.mid || 0,
          asset_type: "option",
          broker: "paper",
          omega_confidence: trade.ociGate?.confidence || 0,
          strategy_id: "omega_zbucks_autopilot",
        }),
      }).then(r => r.json()),
    ]).then(([s, p]) => {
      setSettings(s);
      setPreview(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [trade]);

  const updateComfort = async (level) => {
    try {
      const s = await fetch(`${API}/preview/settings/${USER_ID}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ comfort_level: level }),
      }).then(r => r.json());
      setSettings(s);
    } catch {}
  };

  const submitConfirm = async () => {
    if (!preview || confirming) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/preview/confirm`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ user_id: USER_ID, preview_id: preview.preview_id, confirm_text: "CONFIRM" }),
      }).then(r => r.json());
      setResult(res);
      // Store position in autopilot_positions with full horizon data
      if (trade.decision?.status === "LOCKED") {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/autopilot/execute`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...csrfHeaders() },
          body: JSON.stringify({
            decision: trade.decision,
            vectors: trade.vectors,
            bestHorizon: trade.bestHorizon,
            horizonRank: trade.horizonRank,
            dte: trade.dte,
            strike: trade.strike,
            optionType: trade.optionType,
            expiry: trade.expiry,
          }),
        }).catch(() => {});
      }
    } catch {} finally { setConfirming(false); }
  };

  const rl = READINESS_LABELS[
    preview?.readiness_score >= 85 ? "ELIGIBLE_LIMITED_LIVE"
    : preview?.readiness_score >= 70 ? "MANUAL_CONFIRM_SMALL"
    : preview?.readiness_score >= 55 ? "PREVIEW_ONLY"
    : "ZBUCKS_ONLY"
  ] || READINESS_LABELS.ZBUCKS_ONLY;

  // Determine button/slider color from trade direction
  const tradeColor = trade.optionType?.toLowerCase() === "put" ? "#f87171"
    : trade.optionType?.toLowerCase() === "call" ? "#4ade80"
    : "#a78bfa";

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      <div style={{
        width: 500, maxWidth: "96vw", maxHeight: "92vh", overflowY: "auto",
        background: "#0d1117",
        borderRadius: 18,
        border: `1px solid ${tradeColor}40`,
        padding: 24,
        boxShadow: `0 24px 64px rgba(0,0,0,0.7), 0 0 32px ${tradeColor}20`,
        color: "#e2e8f0",
        fontFamily: "inherit",
      }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0" }}>Real Trade Preview</div>
              {trade.ticker && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
                  background: tradeColor + "20", border: `1px solid ${tradeColor}50`, color: tradeColor }}>
                  {trade.ticker}{trade.strike ? ` $${trade.strike}` : ""} {(trade.optionType || "").toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>Review before any broker order is placed</div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.04)", color: "#94a3b8", fontSize: 18, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}
          >×</button>
        </div>

        {loading ? (
          <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 20, marginBottom: 8, opacity: 0.5 }}>⟳</div>
            Evaluating trade with Omega gates…
          </div>
        ) : result ? (
          /* ── Result view ── */
          <div>
            <div style={{
              padding: 20, borderRadius: 12, textAlign: "center",
              background: result.ok ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)",
              border: `1px solid ${result.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{result.ok ? "✓" : "✗"}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: result.ok ? "#4ade80" : "#f87171" }}>
                {result.ok ? "Order Submitted" : "Order Blocked"}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{result.message}</div>
            </div>
            {result.broker_result && (
              <div style={{ fontSize: 9, color: "#64748b", padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8, marginBottom: 12 }}>
                <div>Broker: {result.broker_result.broker} · Status: {result.broker_result.status}</div>
                <div>Order ID: {result.broker_result.order_id}</div>
              </div>
            )}
            <button onClick={onClose} style={{
              width: "100%", padding: 12, borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)",
              color: "#94a3b8", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            }}>Close</button>
          </div>
        ) : (
          <>
            {/* ── Trade summary ── */}
            {preview && (
              <div style={{
                padding: 14, borderRadius: 10, marginBottom: 14,
                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8, alignItems: "baseline" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0" }}>{preview.symbol}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#4ade80" }}>{preview.side}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>×{preview.quantity} @ ${preview.limit_price?.toFixed(2)}</span>
                  <span style={{ fontSize: 10, color: "#a78bfa" }}>{preview.asset_type}</span>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 10, color: "#94a3b8", flexWrap: "wrap" }}>
                  <span>Notional: <strong style={{ color: "#e2e8f0" }}>${preview.estimated_notional?.toFixed(2)}</strong></span>
                  <span>Max Loss: <strong style={{ color: "#f87171" }}>${preview.max_loss_estimate?.toFixed(2)}</strong></span>
                  <span>OCI Conf: <strong style={{ color: "#e2e8f0" }}>{((preview.omega_confidence || 0) * 100).toFixed(0)}%</strong></span>
                  <span>Readiness: <strong style={{ color: rl.color }}>{(preview.readiness_score || 0).toFixed(0)}/100 — {rl.label}</strong></span>
                </div>
              </div>
            )}

            {/* ── Gate status ── */}
            {preview && (
              <div style={{
                padding: 12, borderRadius: 10, marginBottom: 14,
                background: preview.blocked_reason ? "rgba(248,113,113,0.08)" : "rgba(74,222,128,0.06)",
                border: `1px solid ${preview.blocked_reason ? "rgba(248,113,113,0.25)" : "rgba(74,222,128,0.22)"}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: preview.blocked_reason ? "#f87171" : "#4ade80", marginBottom: 4 }}>
                  {preview.blocked_reason
                    ? "BLOCKED BY OMEGA GATES"
                    : preview.allowed_for_manual_confirm
                    ? "APPROVED — SLIDE TO EXECUTE"
                    : "APPROVED — LIMITED LIVE"}
                </div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>{preview.reason}</div>
                {preview.blocked_reason && (
                  <div style={{ fontSize: 10, color: "#f87171", marginTop: 4 }}>
                    Reason: {preview.blocked_reason.replace(/_/g, " ")}
                  </div>
                )}
              </div>
            )}

            {/* ── Comfortability ── */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>
                Comfortability Level
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {COMFORT_LEVELS.map(c => {
                  const active = settings?.comfort_level === c;
                  return (
                    <button key={c} onClick={() => updateComfort(c)} style={{
                      fontSize: 10, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
                      border: active ? "1px solid #a78bfa" : "1px solid rgba(255,255,255,0.1)",
                      background: active ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.04)",
                      color: active ? "#a78bfa" : "#64748b", fontFamily: "inherit",
                    }}>{c.replace("_", " ")}</button>
                  );
                })}
              </div>
              <div style={{ fontSize: 9, color: "#4a5568", marginTop: 4 }}>
                {COMFORT_DESC[settings?.comfort_level || "LOW"]}
              </div>
            </div>

            {/* ── Settings summary ── */}
            {settings && (
              <div style={{ fontSize: 9, color: "#4a5568", display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16,
                padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                <span>Max risk: ${settings.max_real_risk_dollars}</span>
                <span>Min readiness: {settings.min_readiness_score}/100</span>
                <span>Min OCI: {(settings.min_omega_confidence * 100).toFixed(0)}%</span>
                <span>Manual confirm: {settings.require_manual_confirm ? "Yes" : "No"}</span>
              </div>
            )}

            {/* ── Swipe-to-confirm ── */}
            {preview?.allowed_for_manual_confirm && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", marginBottom: 8,
                  textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Swipe to confirm this order
                </div>
                <SwipeToConfirm
                  onConfirm={submitConfirm}
                  disabled={confirming}
                  color={tradeColor}
                  label={confirming ? "SUBMITTING…" : `SLIDE TO CONFIRM — ${trade.ticker || "ORDER"}`}
                />
              </div>
            )}

            {/* ── Cancel / info ── */}
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button onClick={onClose} style={{
                flex: 1, padding: 11, borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)",
                color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              }}>Cancel</button>
              {preview && !preview.allowed_for_manual_confirm && (
                <div style={{
                  flex: 2, padding: 11, borderRadius: 10, textAlign: "center",
                  border: "1px solid rgba(251,191,36,0.25)", background: "rgba(251,191,36,0.07)",
                  color: "#fbbf24", fontSize: 11, fontWeight: 600,
                }}>
                  Trade not yet eligible for execution
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
